import { useEffect, useState } from 'react'

import { canCrossfade } from '@/lib/motion/motion-preference'
import { SKELETON_DELAY_MS, SKELETON_SWAP_MS } from '@/lib/motion/skeleton'

export interface SkeletonTiming {
  /**
   * `waiting`: loading, inside the delay — lay the skeleton out, do not paint
   * it. `skeleton`: loading, past the delay — paint it. `swap`: the result
   * has replaced a skeleton the member saw, and the two are crossing.
   * `content`: render the result alone.
   *
   * Most callers want the fields below. The phase is for the one that cannot
   * put a class on the box — a skeleton drawn by a child it does not own,
   * which it blanks during `waiting` instead.
   */
  phase: 'waiting' | 'skeleton' | 'swap' | 'content'
  /** Render the skeleton, not the result: true for as long as the load runs. */
  showSkeleton: boolean
  /**
   * Keep the skeleton mounted after the load, as the layer leaving over the
   * content (`SkeletonLayer leaving`): true for the swap only.
   */
  skeletonLeaving: boolean
  /**
   * For the ONE element that holds the skeleton and, later, the content —
   * the same box in both states, so there is nothing to shift:
   *
   *  - `animate-skeleton-in` from the start of the load to the end of the
   *    swap: laid out at once (its box is reserved, so nothing below moves
   *    when the data lands), held invisible for 80ms by the animation's own
   *    delay, then a 220ms fade, ease-in-out, so one cut short by the data
   *    has barely painted. One class for every phase it covers, so the
   *    animation runs on unbroken through the delay and the swap. The wait is
   *    an animation rather than an `opacity-0`, because any animation on the
   *    box would outrank a static opacity and paint the skeleton inside its
   *    wait;
   *  - with `skeleton-swap` beside it while the content replaces a skeleton
   *    the member actually saw: every child but the skeleton's layer fades
   *    in over 220ms, no rise, as the layer fades out over 160ms on top — a
   *    crossfade in one box, never a frame with neither. Content that arrived
   *    inside the delay just appears — there was nothing on screen to
   *    replace — or, at the top of a page still entering, enters with the
   *    page's other sections;
   *  - `''` otherwise.
   *
   * Under reduced motion, and where nothing can animate (jsdom), there is no
   * swap phase at all (`canCrossfade`): the skeleton goes the moment the
   * result lands, and globals.css collapses the appear fade. The delay is not
   * motion, so it stays.
   */
  className: string
  /**
   * For content drawn in a box of its OWN rather than the root's — a
   * branch that replaces the root outright, like CardState's failed and
   * empty states, so no skeleton can stay behind to cross it: a 220ms fade
   * in (`animate-content-in`) during the swap, `''` otherwise.
   */
  revealClassName: string
}

/**
 * The one timing rule for every card-level loading state: CardState,
 * QueryState, and any screen that branches on `resolveViewState` itself.
 *
 * ```
 * isLoading ──┬── < 80ms ── data ──▶ content, no reveal
 *             └── 80ms ── skeleton fades in ── data ──▶ skeleton fades out
 *                                                       over content fading in
 * ```
 *
 * No minimum hold once the skeleton shows, the same as a route's pending page
 * (app/router.tsx): the router can only hold a pending page from the moment it
 * mounts, which would delay every load that lands inside the first 80ms, and
 * the ease-in-out reveal already keeps a short-lived skeleton faint.
 *
 * State is adjusted during render rather than in an effect when `isLoading`
 * flips, the same "derive, don't sync" pattern AppShell uses for its drawer:
 * an effect would paint one frame of the stale branch first, which is the
 * exact frame this hook is about.
 */
export function useSkeletonTiming(isLoading: boolean): SkeletonTiming {
  // Whether the skeleton has been painted in the current loading cycle.
  const [shown, setShown] = useState(false)
  const [wasLoading, setWasLoading] = useState(isLoading)
  // The swap: the cycle just ended with a skeleton on screen.
  const [leaving, setLeaving] = useState(false)

  if (isLoading !== wasLoading) {
    setWasLoading(isLoading)
    // A new cycle starts from scratch: a query that reloads after it once
    // showed its content waits out the delay again before any grey appears.
    if (isLoading) {
      setShown(false)
      setLeaving(false)
    } else {
      // Where there is no crossfade to play, the skeleton just goes.
      setLeaving(shown && canCrossfade())
    }
  }

  useEffect(() => {
    if (!isLoading || shown) return
    const timer = setTimeout(() => {
      setShown(true)
    }, SKELETON_DELAY_MS)
    return () => {
      clearTimeout(timer)
    }
  }, [isLoading, shown])

  // Started after the commit that began the swap has painted, so it never
  // ends before the two animations it is timing have run.
  useEffect(() => {
    if (!leaving) return
    const timer = setTimeout(() => {
      setLeaving(false)
    }, SKELETON_SWAP_MS)
    return () => {
      clearTimeout(timer)
    }
  }, [leaving])

  const phase = isLoading ? (shown ? 'skeleton' : 'waiting') : leaving ? 'swap' : 'content'
  const className = isLoading
    ? 'animate-skeleton-in'
    : leaving
      ? 'animate-skeleton-in skeleton-swap'
      : ''

  return {
    phase,
    showSkeleton: isLoading,
    skeletonLeaving: leaving,
    className,
    revealClassName: leaving ? 'animate-content-in' : '',
  }
}
